import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function CareerPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Career | Bandarban University",
}) {
  const [careers, setCareers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
	const fetchCareers = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/career", payload);
			
			
			// Loop data (array)
			setCareers(response.data || []);
			setFiltered(response.data || []);
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
  
	useEffect(() => {
		fetchCareers();
	}, []);
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  // Search filter
  useEffect(() => {
    let result = careers;
    if (search) {
      result = careers.filter(
        (c) =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.department.toLowerCase().includes(search.toLowerCase()) ||
          c.location.toLowerCase().includes(search.toLowerCase())
      );
    }
    setFiltered(result);
  }, [search, careers]);

  return (
    <div className="bg-gray-50 text-gray-800">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-teal-600 text-white py-16 text-center">
        <h1 className="text-4xl font-bold">Career Opportunities</h1>
        <p className="mt-2 text-lg opacity-90">
          Join our team and shape your future with us.
        </p>
      </section>

      {/* Career Table Section */}
      <section className="max-w-6xl mx-auto py-16 px-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Available Positions</h2>
          <input
            type="text"
            placeholder="Search careers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg px-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading careers...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : filtered.length > 0 ? (
          <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
            <table className="min-w-full table-auto">
              <thead className="bg-teal-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Title</th>
                  <th className="px-6 py-3 text-left">Department</th>
                  <th className="px-6 py-3 text-left">Specialization</th>
				  <th className="px-6 py-3 text-left">Opening Date</th>
                  <th className="px-6 py-3 text-left">Deadline</th>
                  <th className="px-6 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((career) => (
                  <tr
                    key={career.id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-6 py-4">{career.designation_name}</td>
                    <td className="px-6 py-4">{career.department_name}</td>
                    <td className="px-6 py-4">{career.subject && career.subject.trim() !== "" ? career.subject : "Relevant"}</td>
					<td className="px-6 py-4">
                      {new Date(career.start_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(career.end_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Link
                        to={`/career-details/${career.id}`}
                        className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500">No careers found.</p>
        )}
      </section>

      {/* Contact Section */}
      <section className="max-w-6xl mx-auto py-16 px-6 grid md:grid-cols-3 gap-8 text-center">
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Mail className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Email</h3>
          <p className="text-gray-600">registrar@bubban.ac.bd</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Phone className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Phone</h3>
          <p className="text-gray-600">+880-131-8305900</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <MapPin className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Office Address</h3>
          <p className="text-gray-600">HR Dept, Rowadaw Heights Main Road, Bandarban</p>
        </div>
      </section>
    </div>
  );
}
